/* eslint-disable no-underscore-dangle */
import React, { useRef, useState, useEffect, useCallback, createContext } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import { ThemeProvider } from 'styled-components';


import AppErrorBoundary from '@components/system/errorBoundary';

import smoothscroll from 'smoothscroll-polyfill';
import SessionTimeoutAlert from '../components/common/miscellaneous/SessionTimeoutAlert';
import setupTimeoutResetsOnUserActivity from '../util/setupTimeoutResetsOnUserActivity';
import deactivateTimeoutCountdown from '../util/deactivateTimeoutCountdown';
import initializeTimeoutCountdown from '../util/initializeTimeoutCountdown';
import User from '../api/user';

import { redirectUser } from '../util/redirectUser';
import { dashboardPaths, deleteCookie, destroyClientCookie, getCookieFromClient } from '../util';
import 'react-phone-number-input/style.css';
import { theme } from '../styled/theme';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/base.css';
import useInitUser from 'hooks/useInitUser';
import ProfileProvider from 'context/profile/provider';
import { IS_CLIENT_MODE } from 'util/constants';
import useBugsnag from 'hooks/useBugsnag';
import useApiCheckout from 'hooks/useApiCheckout';
import { ToastContainer } from 'react-toastify';
import { FiX } from 'react-icons/fi';


export const StoreContext = createContext(null);

function resetTimeout(params = {}) {
  const { showAlertTimeout, endSessionTimeout, showTimeoutAlert = () => {}, logout = () => {} } = params;

  clearTimeout(showAlertTimeout);
  clearTimeout(endSessionTimeout);
  showTimeoutAlert(false);

  initializeTimeoutCountdown({ showTimeoutAlert, logout });
}


function ExpressPayWebsite({ Component, pageProps }) {

  const router = useRouter();
  const timeoutsRef = useRef({ showAlertTimeout: undefined, endSessionTimeout: undefined });
  const [showTimeoutAlert, setShowTimeoutAlert] = useState(false);
  const [windowEventListeners, setWindowEventListeners] = useState([]);
  const eventListenerFunctionRef = useRef(() => {});
  const { showAlertTimeout, endSessionTimeout } = timeoutsRef.current || {};

  const [user_, setUser_] = useState();
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
 
  const user = useInitUser([loggedIn]);

  const [authenticationRequired, setAuthenticationRequired] = useState(user ? false : true);
  const [authenticationCompleted, setAuthenticationCompleted] = useState(user ? true : false);


  // console.log('user_', user_);
  // console.log('loggedIn', loggedIn);
  // console.log('loading', loading);

  useEffect(() => storePathValues, [router.asPath]);

  function storePathValues() {
    const isClientSide = typeof window !== "undefined";
    if(isClientSide){
      const storage = window.sessionStorage; 
      if (!storage) return;
      // Set the previous path as the value of the current path.
      const prevPath = storage.getItem("currentPath");
      storage.setItem("prevPath", prevPath);
      // Set the current path value by looking at the browser's location object.
      storage.setItem("currentPath", window.location.pathname);
    }
  }
  
  const { isApiCheckout, guestAuthSuccessful } = useApiCheckout();


  const refreshAuth = useCallback(async (user, isApiCheckout, guestAuthSuccessful) => {
    console.log('refreshAuth is called,|| user || isApiCheckout || guestAuthSuccessful', user, isApiCheckout, guestAuthSuccessful);
    if (user) {
      setUser_(user);
      setLoggedIn(true);
    }else {
      try {
        setLoading(true);
        if (!isApiCheckout || (isApiCheckout && guestAuthSuccessful)) {
          console.log('hap 2');
          const userRes = await User.isLoggedIn();
          if (userRes) {
            setUser_(userRes);
            setLoggedIn(true);
          } else {
            setLoggedIn(false);
          }
        }
      } catch (err) {
        setLoggedIn(false);
      } finally {
        setLoading(false);
      }
    }
 
  }, [setUser_, setLoggedIn, setLoading]);

  useEffect(() => {
    const cookies = IS_CLIENT_MODE && getCookieFromClient("epay_usr");
    const userFromClient = cookies ? JSON.parse(cookies) : null;
    refreshAuth(userFromClient, isApiCheckout, guestAuthSuccessful);
  }, [refreshAuth, isApiCheckout, guestAuthSuccessful]);

  useEffect(() => {
    // initialize the smooth-scroll polyfill!
    smoothscroll.polyfill();

    // initialise Bugsnag
    // NOTE: this method needs to be called only once.
    // In the future where this useEffect gets a dependency state variable,
    // this initiation call must be moved to another useEffect without any dependencies
    // or its very own useEffect
    useBugsnag().start();

    if (user) {
      // eslint-disable-next-line no-use-before-define
      _initializeTimeoutCountdown();
    }
    const consoleLogWarningAndBuild = () => {
      // eslint-disable-next-line no-empty
      // eslint-disable-next-line no-undef
      if (typeof __NEXT_DATA__ === 'object') {
        console.info('%cStop!', 'color: #F76E1E; font-size: 30px; font-weight: bold;');
        console.info('%cDO NOT TYPE OR PASTE ANYTHING HERE', 'color: #F76E1E; font-size: 18px; font-weight: bold;');
        // eslint-disable-next-line no-undef
        console.info(`%cVERSION=${__NEXT_DATA__.buildId.slice(0, 8)}`, 'color: #047CDE; font-size: 14px;');
      }
    };

    consoleLogWarningAndBuild();
  }, []);

  // eslint-disable-next-line no-underscore-dangle
  const _deactivateTimeoutCountdown = () => {
    deactivateTimeoutCountdown({ showAlertTimeout, endSessionTimeout });
    windowEventListeners.forEach((each) => {
      document.removeEventListener(each, resetTimeout);
    });
  };

  const logout = async (callback) => {
    try {
      await User.logout();
      setLoggedIn(false);
      setUser_(null);
      setAuthenticationRequired(true);
      setAuthenticationCompleted(false);
      deleteCookie("epay_usr")
      destroyClientCookie('ep_usr');
      destroyClientCookie('PHPSESSID');
      setShowTimeoutAlert(false);
      _deactivateTimeoutCountdown();
      if (typeof callback === 'function') {
        callback();
      } else {
        router.push('/');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleAuthSuccess = (user, url) => {
    console.log('handleAuthSuccessCalled');
    setLoggedIn(true);
    setAuthenticationRequired(false);
    setAuthenticationCompleted(true);
    if (url === 'none') return;
    if (!url) {
      return router.push(url);
    }
    router.push('/');
  };

  const globalProps = {
    logout,
    handleAuthSuccess,
  };

  const _setupTimeoutResetsOnUserActivity = () => {
    setupTimeoutResetsOnUserActivity({
      showAlertTimeout: timeoutsRef.current.showAlertTimeout,
      endSessionTimeout: timeoutsRef.current.endSessionTimeout,
      showTimeoutAlert: setShowTimeoutAlert,
      logout,
      resetTimeout,
      setWindowEventListeners,
      setEventListenerFunctionRef: (fn) => (eventListenerFunctionRef.current = fn),
    });
  };

  const _initializeTimeoutCountdown = () => {
    initializeTimeoutCountdown({
      showTimeoutAlert: setShowTimeoutAlert,
      logout,
      setShowAlertTimeout: (timeout) => {
        timeoutsRef.current.showAlertTimeout = timeout;
      },
      setEndSessionTimeout: (timeout) => {
        timeoutsRef.current.endSessionTimeout = timeout;
      },
    });
    _setupTimeoutResetsOnUserActivity();
  };



  return (
    <>
      <Head>
        <title>expressPay</title>
        <meta
            name="viewport"
            content="width=device-width, initial-scale=1, shrink-to-fit=no"
          />
      </Head>
      <AppErrorBoundary>
        <StoreContext.Provider
          value={{
            showAlertTimeout,
            endSessionTimeout,
            setWindowEventListeners,
            windowEventListeners,
            showTimeoutAlert: setShowTimeoutAlert,
            initializeTimeoutCountdown: _initializeTimeoutCountdown,
            deactivateTimeoutCountdown: _deactivateTimeoutCountdown,
            setupTimeoutResetsOnUserActivity: _setupTimeoutResetsOnUserActivity,
            logout,
            isApiCheckout,
            auth: {
              authenticationRequired,
              setAuthenticationRequired,
              authenticationCompleted,
              setAuthenticationCompleted,
            },
            auth_: {
              user: user_,
              loggedIn,
              loading,
              refreshAuth,
              guestAuthSuccessful,
            },
          }}
        >
          <ProfileProvider>
            <ThemeProvider theme={theme}>
             {loggedIn ? (
              <SessionTimeoutAlert loggedIn={loggedIn}/>
            ) : null}
              <Component user={user_} {...pageProps} {...globalProps} handleLogout={logout} />
              <ToastContainer
                position="top-center"
                hideProgressBar
                closeButton={<div style={{
                  width: '20px',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: '50%',
                  right: '10px',
                  transform: 'translateY(-50%)',
                }}>
                  <FiX color="#fff" size={16} />
                </div>}
                autoClose={2000}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                icon={({ type }) => {
                  switch (type) {
                    case 'success':
                      return <img src="/images/icons/toast/check.svg" alt="success" />;
                    case 'error':
                      return <img src="/images/icons/toast/error.svg" alt="error" />;
                    case 'warning':
                      return <img src="/images/icons/toast/warn.svg" alt="warning" />;
                    default:
                      return null;
                  }
                }}
              />
              <style jsx global>{`
                .Toastify__toast {
                  color: #fff !important;
                }
                .Toastify__toast-theme--colored.Toastify__toast--success {
                  background: #41CB93 !important;
                }
                .Toastify__toast-theme--colored.Toastify__toast--error {
                  background: #E01E40 !important;
                }
                .Toastify__toast-theme--colored.Toastify__toast--warning {
                  background: #F88F1C !important;
                }
                .Toastify__toast-theme--colored.Toastify__toast--info {
                  background: #2b6cb0 !important;
                }
                .Toastify__toast-theme--colored.Toastify__toast--default {
                  background: #F88F1C !important;
                }
                .Toastify__toast-icon img {
                  width: 20px;
                  height: 20px;
                }
            `}</style>
            </ThemeProvider>
          </ProfileProvider>
        </StoreContext.Provider>
      </AppErrorBoundary>
    </>
  );
}

ExpressPayWebsite.getInitialProps = async ({ Component, ctx }) => {
  // console.log("ctx", ctx);
  const cookies = parseCookies(ctx);
  // console.log("cookies", cookies);

  let pageProps = {};
  const { ep_usr: epUsr } = cookies;

  //Adding this to prevent ctx errors because of static generation which does not produce res and req on context
  ctx.res = { writeHead: () => {}, end: () => {} };

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  if (!epUsr) {
    const dashboardUrls = Object.values(dashboardPaths).flat();
    const isProtectedRoute = dashboardUrls.includes(ctx.pathname);
    if (isProtectedRoute) {
      redirectUser(ctx, '/login');
    }
  }

  return {
    pageProps,
  };
};

function CustWithRouter({ props }) {
  const pr = { ...props };
  pr.router = useRouter();
  return <ExpressPayWebsite {...pr} />;
}
// export default CustWithRouter;
export default ExpressPayWebsite;
